import { useState } from 'react';
import { router } from '@inertiajs/react';
import type { SalesminerOpportunity } from '@/types/serializers';
import MetricsCards from '@/components/opportunities/MetricsCards.tsx';
import OpportunitiesGrid from '@/components/opportunities/OpportunitiesGrid.tsx';
import OpportunitiesSearch from '@/components/opportunities/OpportunitiesSearch.tsx';
import ResultsSummary from '@/components/opportunities/ResultsSummary.tsx';
import { salesminer_app_opportunity_path } from '@/routes';
import SalesminerAppLayout from "@/salesminer/layouts/SalesminerAppLayout.tsx";

interface OpportunitiesPageProps {
    opportunities: SalesminerOpportunity[];
    search: string;
    filters: {
        new: boolean;
        starred: boolean;
        relevant: boolean;
        discarded: boolean;
        exported: boolean;
    };
    metrics: {
        total: number;
        new: number;
        starred: number;
        relevant: number;
        discarded: number;
        exported: number;
    };
}


export default function OpportunityIndex({opportunities, search, filters, metrics}: OpportunitiesPageProps) {
    const [searchTerm, setSearchTerm] = useState(search);
    const [showFilters, setShowFilters] = useState(false);
    const [activeMetricFilter, setActiveMetricFilter] = useState<string | null>(null);

    // Extract organization slug from current URL
    const organizationSlug = window.location.pathname.split('/')[2];

    const handleSearch = (value: string) => {
        setSearchTerm(value);
        router.visit(window.location.pathname, {
            data: {search: value, filter: filters},
            only: ['opportunities'],
            preserveState: true
        });
    };

    const handleMetricCardClick = (filterKey: string) => {
        if (activeMetricFilter === filterKey) {
            // Clicking the same card clears the filter (show all)
            setActiveMetricFilter(null);
            const newFilters = {new: false, starred: false, relevant: false, discarded: false, exported: false};
            router.visit(window.location.pathname, {
                data: {search: searchTerm, filter: newFilters},
                only: ['opportunities'],
                preserveState: true
            });
        } else {
            // Clicking a different card sets only that filter (radio behavior)
            setActiveMetricFilter(filterKey);
            const newFilters = {
                new: filterKey === 'new',
                starred: filterKey === 'starred',
                relevant: filterKey === 'relevant',
                discarded: filterKey === 'discarded',
                exported: filterKey === 'exported'
            };
            router.visit(window.location.pathname, {
                data: {search: searchTerm, filter: newFilters},
                only: ['opportunities'],
                preserveState: true
            });
        }
    };

    // Use metrics from Rails instead of calculating from filtered opportunities

    return <SalesminerAppLayout>
        <div className="p-6">
            <div className="max-w-[1400px] mx-auto">
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                    Opportunities
                </h1>
                <p className="text-slate-600 dark:text-slate-400 mb-8">
                    Discover and manage sales opportunities across all clients
                </p>

                {/* Key Metrics */}
                <MetricsCards
                    metrics={metrics}
                    onMetricClick={handleMetricCardClick}
                    activeMetricFilter={activeMetricFilter}
                />

                {/* Search and Filter Bar */}
                <OpportunitiesSearch
                    searchTerm={searchTerm}
                    onSearchChange={handleSearch}
                    showFilters={showFilters}
                    onToggleFilters={() => setShowFilters(!showFilters)}
                />

                {/* Filter Panel - Placeholder for now */}
                {showFilters && (
                    <div
                        className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-6 mb-6">
                        <h3 className="text-sm font-medium text-slate-900 dark:text-white mb-4">Advanced Filters</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Filter options will be implemented
                            here...</p>
                    </div>
                )}

                {/* Results Summary */}
                <ResultsSummary
                    count={opportunities.length}
                    searchTerm={searchTerm}
                    onClearSearch={() => handleSearch('')}
                />

                {/* Index Cards */}
                <OpportunitiesGrid
                    opportunities={opportunities}
                    onOpportunityClick={(id) => router.visit(salesminer_app_opportunity_path(organizationSlug, id))}
                    searchTerm={searchTerm}
                />
            </div>
        </div>
    </SalesminerAppLayout>
}